import React from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import {
  ComponentCPTCell,
  CPTCell,
  getCptExportColumns,
  getPmServiceExportColumns,
  isPmServiceDetailsLayout
} from "./PatientListTable";

const { ExportCSVButton } = CSVExport;

const PatientListModal = ({
  isOpen,
  onToggle,
  contextMeta,
  statusLabel,
  displayMonth,
  totalApiPatients,
  patientRows,
  cptExportCodes = [],
  columns,
  showPatientListLoading,
  patientSearchQuery,
  onSearchChange,
  onClearSearch,
  patientListPage,
  patientListLimit,
  onPageChange,
  onSizePerPageChange,
  renderLoading,
  cptDetailsLayout = "default"
}) => {
  const totalPatients = patientRows.length;
  const totalPages = totalPatients > 0 ? Math.ceil(totalPatients / patientListLimit) : 1;
  const activePage = Math.min(patientListPage, totalPages);

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    bootstrap4: true,
    page: activePage,
    sizePerPage: patientListLimit,
    totalSize: totalPatients,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    onPageChange,
    onSizePerPageChange,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
    showTotal: totalPatients > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 }
    ]
  };

  const cptStatusFilter = statusLabel === "MET" ? "met" : statusLabel === "NOT MET" ? "notMet" : null;
  const isClinicalPmLayout = isPmServiceDetailsLayout(cptDetailsLayout);

  const cptDetailsFormatter = (cell, row) => {
    const hasCptCodes =
      row?.cptCodes && typeof row.cptCodes === "object" && Object.keys(row.cptCodes).length > 0;

    if (hasCptCodes) {
      return (
        <ComponentCPTCell
          cptCodes={row.cptCodes}
          additionalDetails={row.additionalDetails}
          cptStatusFilter={cptStatusFilter}
          layout={cptDetailsLayout}
        />
      );
    }

    if (row?.additionalDetails && typeof row.additionalDetails === "object") {
      return (
        <CPTCell
          details={row.additionalDetails}
          cptStatusFilter={cptStatusFilter}
          layout={cptDetailsLayout}
        />
      );
    }

    return cell || "-";
  };

  const baseTableColumns = columns
    .filter((column) => column.dataField !== "statusLabel")
    .map((column) => {
      if (column.dataField !== "additionalDetailsText") {
        return column;
      }

      return {
        ...column,
        formatter: cptDetailsFormatter,
        headerStyle: () => ({ minWidth: "360px" })
      };
    });

  const tableColumns = baseTableColumns;

  const exportColumns = [
    ...baseTableColumns.map((column) => {
      if (
        column.dataField === "additionalDetailsText" ||
        column.dataField === "clinicalRpmTimeDisplay" ||
        column.dataField === "clinicalCcmTimeDisplay" ||
        column.dataField === "technicalAdherenceDaysDisplay" ||
        column.dataField === "billableCptCodesDisplay"
      ) {
        return { ...column, csvExport: false };
      }
      return column;
    }),
    ...(isClinicalPmLayout ? getPmServiceExportColumns(cptDetailsLayout) : getCptExportColumns(cptExportCodes))
  ];

  return (
    <Modal isOpen={isOpen} toggle={onToggle} size="lg" centered className="cpt-patient-list-modal">
      <ModalHeader className="cpt-patient-modal-header-with-legend">
        <div className="cpt-patient-list-modal-title-wrap">
          <div className="cpt-patient-list-modal-header">
            {contextMeta?.badgeLabel && (
              <>
                <span className="cpt-patient-code-badge" style={{ background: contextMeta.badgeColor }}>
                  {contextMeta.badgeLabel}
                </span>
                {contextMeta.supportingBadge && <span className="cpt-patient-program-badge neutral">{contextMeta.supportingBadge}</span>}
              </>
            )}
            <span className="cpt-patient-status-label">{statusLabel} Patients</span>
          </div>
          <button type="button" className="cpt-patient-close-btn" onClick={onToggle} aria-label="Close patient list modal">
            <i className="mdi mdi-close"></i>
            <span>Close</span>
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="bg-white">
        <div className="patient-list-modal-subtitle">
          <div className="cpt-patient-list-info">
            {contextMeta?.title && <div className="cpt-patient-list-title">{contextMeta.title}</div>}
            {contextMeta?.description && <div className="cpt-patient-list-desc">{contextMeta.description}</div>}
          </div>
          <div className="cpt-patient-list-meta">
            {displayMonth && <span><i className="mdi mdi-calendar"></i> {displayMonth}</span>}
            {showPatientListLoading ? (
              <span><i className="mdi mdi-timer-sand"></i> Loading patients...</span>
            ) : (
              <span><i className="mdi mdi-account-group"></i> {totalApiPatients} Patients</span>
            )}
          </div>
        </div>
        <ToolkitProvider
          keyField="_id"
          data={patientRows}
          columns={exportColumns}
          exportCSV={{ fileName: `${contextMeta.filePrefix}-${statusLabel.toLowerCase()}-patients.csv` }}
        >
          {(toolkitProps) => (
            <div>
              {!showPatientListLoading && (
                <div className="patient-list-toolbar">
                  <div className="patient-list-search-shell">
                    <span className="patient-list-search-icon">
                      <i className="mdi mdi-magnify"></i>
                    </span>
                    <input
                      type="text"
                      className="patient-list-search-input"
                      placeholder="Search by name, email, organization, provider, or details"
                      value={patientSearchQuery}
                      onChange={onSearchChange}
                    />
                    {patientSearchQuery && (
                      <button type="button" className="patient-list-search-clear" onClick={onClearSearch} aria-label="Clear search">
                        <i className="mdi mdi-close-circle"></i>
                      </button>
                    )}
                  </div>
                  <ExportCSVButton {...toolkitProps.csvProps} className="patient-list-export-btn">
                    <span className="patient-list-export-icon">
                      <i className="mdi mdi-file-excel-outline"></i>
                    </span>
                    <span>Export CSV</span>
                  </ExportCSVButton>
                </div>
              )}
              <BootstrapTable
                {...toolkitProps.baseProps}
                columns={tableColumns}
                bootstrap4
                wrapperClasses="table-responsive"
                bordered={false}
                rowClasses="patient-list-row-animate"
                pagination={paginationFactory(paginationOptions)}
                noDataIndication={() => {
                  if (showPatientListLoading) {
                    return renderLoading();
                  }

                  return (
                    <div className="patient-list-empty-state">
                      <div className="patient-list-empty-icon">
                        <i className="mdi mdi-account-search-outline"></i>
                      </div>
                      <div className="patient-list-empty-title">No patients found</div>
                      <div className="patient-list-empty-text">
                        No {statusLabel} patients found for {contextMeta.emptyLabel} in {displayMonth}.
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
        </ToolkitProvider>
      </ModalBody>
    </Modal>
  );
};

export default PatientListModal;
